import type { BaseInstance, ModelName } from '@pigello/pigello-matrix';
import type { ListResponse } from '../types';
import { aggregateNestedData, chunkArray, extractNestedIds } from './utils';
import { getConfig } from '@/lib/get-config';
import { useQueries } from '@tanstack/react-query';
import { getList } from '../api/get-list';

export function useFetchNestedQueries<TInstance extends BaseInstance>(
  modelName: ModelName,
  mainQueryData: ListResponse<TInstance>['list'],
  filteredNested: Array<keyof TInstance>,
  fetchAllManyRelations: boolean
) {
  /**
   * Fetch configuration based on the provided model name.
   */
  const config = getConfig<ModelName, TInstance>(modelName, true);

  /**
   * Extract nested IDs from the main list to identify which nested relations need to be fetched.
   */
  const nestedIds = extractNestedIds(
    mainQueryData,
    filteredNested,
    fetchAllManyRelations
  );

  /**
   * Prepare the list of nested queries to be fetched based on the extracted IDs.
   */

  const nestedToFetch = Array.from(nestedIds).map(([fieldName, idsList]) => {
    const fieldConfig = config?.fields[fieldName];
    // Ensure the field is a valid relation field.
    if (!fieldConfig || !('relationConfig' in fieldConfig)) {
      throw new Error(
        `Field ${String(fieldName)} is not a relation field in model ${modelName}`
      );
    }
    const relationModelName = fieldConfig.relationConfig;
    const batchedIds = chunkArray(idsList, 40);

    /**
     * Special-case: monitoring models reference their parent via mtParentInstance.
     * This maps to the parent model’s config to fetch correct relations.
     */
    if (modelName.endsWith('monitoring') && fieldName === 'mtParentInstance') {
      return {
        fieldName,
        relationModelName: config.parentConfig as ModelName,
        batchedIds,
      };
    }
    return {
      fieldName,
      relationModelName,
      batchedIds,
    };
  });

  /**
   * Use React Query's useQueries to fetch all nested relations in parallel.
   */
  return useQueries({
    queries: nestedToFetch.flatMap(
      ({ fieldName, relationModelName, batchedIds }) => {
        const batchedQueries = [];
        for (const [index, batch] of batchedIds.entries()) {
          batchedQueries.push({
            queryKey: [
              'nested',
              relationModelName,
              fieldName,
              index,
              batch.join(','),
            ],
            queryFn: async () => {
              const { list } = await getList<TInstance>({
                modelName: relationModelName,
                queryParams: {
                  filters: {
                    id: { __in: batch.join(',') },
                  },
                  pageSize: batch.length,
                },
              });
              return { fieldName, data: list };
            },
            enabled: batch.length > 0,
          });
        }

        return batchedQueries;
      }
    ),
    // Allows us to get a stable aggregated return value instead of useQueries
    // default return value which is not stable.
    //! Key to not causing render loops!
    combine(result) {
      const data = aggregateNestedData(result);

      const isLoading = result.some((q) => q.isLoading);
      const isFetching = result.some((q) => q.isFetching);
      const isPending = result.some((q) => q.isPending);
      const isSuccess = result.every((q) => q.isSuccess);
      const isError = result.some((q) => q.isError);
      const isRefetching = result.some((q) => q.isRefetching);
      const isStale = result.every((q) => q.isStale);

      return {
        data,
        isPending,
        isSuccess,
        isFetching,
        isLoading,
        isError,
        isRefetching,
        isStale,
      };
    },
  });
}
